import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import RemoveModeratorOutlinedIcon from "@mui/icons-material/RemoveModeratorOutlined";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useState } from "react";
import { useLocalState } from "../../../utils/hooks/global/useLocalStorage";
import CustomButton from "../../global/CustomButton";

// Superadmins promote a user to admin or demote an admin, after a
// confirmation. onChange receives the updated user.
const UserRoleButton = ({ user, onChange }) => {
  const [jwt] = useLocalState(null, "jwt");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const promote = !user.isAdmin;

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/users/${user.id}/role`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + jwt,
          },
          body: JSON.stringify({ isAdmin: promote }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || `Request failed (${response.status})`);
      }
      setOpen(false);
      onChange?.(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CustomButton
        buttonText={promote ? "Make admin" : "Remove admin"}
        icon={
          promote ? AdminPanelSettingsOutlinedIcon : RemoveModeratorOutlinedIcon
        }
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      />
      <Dialog
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow:
              "rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px",
            width: "420px",
            maxWidth: "90%",
          },
        }}
        open={open}
        onClose={() => !saving && setOpen(false)}
      >
        <DialogTitle>
          {promote ? "Make" : "Remove"} {user.displayName}{" "}
          {promote ? "an admin" : "as admin"}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {promote
              ? "Admins can manage entities, channels, announcements, partners and status checks."
              : "They keep their account but lose access to the admin pages and actions."}{" "}
            The change applies from their next action in the app.
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CustomButton
            onClick={() => setOpen(false)}
            buttonText="Cancel"
            disabled={saving}
          />
          <CustomButton
            onClick={handleConfirm}
            buttonText={saving ? "Saving…" : promote ? "Make admin" : "Remove admin"}
            color={promote ? "black" : "lRed"}
            disabled={saving}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserRoleButton;
